import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { IModule, IModuleDetail } from "../../../types/module";

// 📌 Estado inicial
export interface IModuleState {
    modules: IModule[];             // Lista general de módulos
    moduleDetail: IModuleDetail | null; // Detalle de un módulo específico
    loading: boolean;
}

export const initialState: IModuleState = {
    modules: [],
    moduleDetail: null,
    loading: false
};

// 📌 Slice
export const moduleSlice = createSlice({
    name: "module",
    initialState,
    reducers: {

        // ======================
        // 🌐 Básicos
        // ======================
        setModules: (state, action: PayloadAction<IModule[]>) => {
            state.modules = action.payload;
        },

        setModuleDetail: (state, action: PayloadAction<IModuleDetail | null>) => {
            state.moduleDetail = action.payload;
        },

        clearModuleDetail: (state) => {
            state.moduleDetail = null;
        },

        resetModuleState: () => initialState,

        // ======================
        // 🎯 Reducers granulares
        // ✅ SOLO actualizan el moduleDetail
        // ======================

        updateModuleNameSuccess: (
            state,
            action: PayloadAction<{ newName: string }>
        ) => {
            if (state.moduleDetail) {
                state.moduleDetail.name = action.payload.newName;
            }
        },

        updateModuleCategorySuccess: (
            state,
            action: PayloadAction<{ categoryId: number }>
        ) => {
            if (state.moduleDetail) {
                state.moduleDetail.categoryId = action.payload.categoryId;
            }
        },

        updateModuleOrderSuccess: (
            state,
            action: PayloadAction<{ newOrder: number }>
        ) => {
            if (state.moduleDetail) {
                state.moduleDetail.orderIndex = action.payload.newOrder;
            }
        },

        updateModuleRolesSuccess: (
            state,
            action: PayloadAction<{ roles: string[] }>
        ) => {
            if (state.moduleDetail) {
                state.moduleDetail.roles = action.payload.roles;
            }
        },

        updateModuleIconSuccess: (
            state,
            action: PayloadAction<{ newIcon: string }>
        ) => {
            if (state.moduleDetail) {
                state.moduleDetail.icon = action.payload.newIcon;
            }
        },

        updateModuleRouteSuccess: (
            state,
            action: PayloadAction<{ newRoute: string }>
        ) => {
            if (state.moduleDetail) {
                state.moduleDetail.route = action.payload.newRoute;
            }
        },

        toggleModuleStatusSuccess: (
            state,
            action: PayloadAction<{ disabled: boolean }>
        ) => {
            if (state.moduleDetail) {
                state.moduleDetail.disabled = action.payload.disabled;
            }
        },

        updateModuleHasChildrenSuccess: (
            state,
            action: PayloadAction<{ hasChildren: boolean }>
        ) => {
            if (state.moduleDetail) {
                state.moduleDetail.hasChildren = action.payload.hasChildren;
            }
        },

        /**
         * ✏️ Actualiza un campo individual dentro del detalle del módulo.
         *
         * @param state - Estado actual del slice.
         * @param action - Objeto con la propiedad `field` (clave del módulo) y `value` (nuevo valor).
         *
         * ✅ Ideal para:
         *  - Cambios locales en inputs o selects del detalle.
         *  - Evitar mutaciones directas o duplicar estados de formulario.
         */
        updateModuleField(
            state,
            action: PayloadAction<{ field: keyof IModuleDetail; value: any }>
        ) {
            const { field, value } = action.payload;

            if (state.moduleDetail) {
                state.moduleDetail = {
                    ...state.moduleDetail,
                    [field]: value,
                };
            }
        },

        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },

    },
});

// 📌 Exportar actions
export const {
    setModules,
    setModuleDetail,
    clearModuleDetail,
    resetModuleState,
    updateModuleNameSuccess,
    updateModuleCategorySuccess,
    updateModuleOrderSuccess,
    updateModuleRolesSuccess,
    updateModuleIconSuccess,
    updateModuleRouteSuccess,
    toggleModuleStatusSuccess,
    updateModuleHasChildrenSuccess,
    updateModuleField,
    setLoading,
} = moduleSlice.actions;

// 📌 Exportar reducer
export default moduleSlice.reducer;
